import { ethers } from 'ethers';
import { formatNumber } from './commonFunction';
import {
    getIncomingPaymentRequestMeta,
    getPaymentRequestMeta,
} from './paymentRequestNotes';

const USDC_FALLBACK = {
    name: 'USDC',
    symbol: 'USDC',
    decimals: 6,
    logoURI: '',
    address: '0x3600000000000000000000000000000000000000',
};

const normalizeAddr = (value) => String(value || '').trim().toLowerCase();

export const resolveTokenInfo = (tokenAddress, tokenList = [], record = {}) => {
    const needle = normalizeAddr(tokenAddress);
    const fromList =
        tokenList.find(
            (token) => normalizeAddr(token.address) === needle,
        ) || null;

    if (fromList) return fromList;

    const decimals =
        record.tokenDecimals != null && Number.isFinite(Number(record.tokenDecimals))
            ? Number(record.tokenDecimals)
            : 6;
    const symbol = record.tokenSymbol || 'TOKEN';

    return {
        name: symbol,
        symbol,
        decimals,
        logoURI: USDC_FALLBACK.logoURI,
        address: tokenAddress || USDC_FALLBACK.address,
    };
};

const amountFromRecord = (record, decimals) => {
    if (record?.amountWei) {
        try {
            return parseFloat(ethers.formatUnits(String(record.amountWei), decimals));
        } catch (_error) {
            // fall through
        }
    }
    const n = parseFloat(record?.amount);
    return Number.isFinite(n) ? n : 0;
};

/**
 * Map MySQL directPaymentRecords into dashboard history row shapes.
 * Always returns a row when paymentId/amount exist — never drop for missing token list entry.
 */
export const mapDirectPaymentRecordToHistoryRow = (
    record,
    { role, walletAddress, tokenList = [] } = {},
) => {
    if (!record) return null;

    const kind = String(record.kind || 'direct').toLowerCase();
    const paymentId = Number(record.paymentId);
    if (!Number.isFinite(paymentId)) return null;

    const wallet = normalizeAddr(walletAddress);
    const sender = normalizeAddr(record.sender);
    const receiver = normalizeAddr(record.receiver);

    if (role === 'sent' && sender !== wallet) return null;
    if (role === 'received' && receiver !== wallet) return null;
    if (role === 'all' && sender !== wallet && receiver !== wallet) return null;

    const tokenInfo = resolveTokenInfo(record.tokenAddress, tokenList, record);
    const parsedNumber = amountFromRecord(record, tokenInfo.decimals);
    const status = String(record.status || '').toLowerCase();
    const createdMs = record.createdAt
        ? new Date(record.createdAt).getTime()
        : null;

    if (kind === 'tracked') {
        const finished = status === 'claimed' || status === 'refunded';
        const disputed = status === 'disputed';
        return {
            id: paymentId,
            paymentId,
            sender: record.sender,
            receiver: record.receiver,
            tokenAddress: record.tokenAddress,
            tokenName: tokenInfo.name,
            tokenImage: tokenInfo.logoURI,
            tokenDecimal: tokenInfo.decimals,
            amount: formatNumber(parsedNumber),
            parsedNumber,
            remainingEscrowFloat: finished ? 0 : parsedNumber,
            type: 'Milestone',
            paymentTime:
                Number.isFinite(createdMs) && createdMs > 0
                    ? Math.floor(createdMs / 1000)
                    : undefined,
            createdAt: record.createdAt,
            createdAtMs: Number.isFinite(createdMs) ? createdMs : undefined,
            isClaimed: finished,
            isDisputeRaised: disputed,
            milestonesNum: Number(record.milestoneCount || 0) || undefined,
            claimedMilestonesNum: finished
                ? Number(record.milestoneCount || 0) || undefined
                : undefined,
            txHash: record.txHash,
            claimTxHash: record.claimTxHash,
            status,
            source: 'db',
        };
    }

    return {
        id: paymentId,
        paymentId,
        sender: record.sender,
        receiver: record.receiver,
        tokenAddress: record.tokenAddress,
        tokenName: tokenInfo.name,
        tokenImage: tokenInfo.logoURI,
        tokenDecimal: tokenInfo.decimals,
        amount: formatNumber(parsedNumber),
        parsedNumber,
        type: 'Direct',
        paymentTime:
            Number.isFinite(createdMs) && createdMs > 0
                ? Math.floor(createdMs / 1000)
                : undefined,
        createdAt: record.createdAt,
        createdAtMs: Number.isFinite(createdMs) ? createdMs : undefined,
        isClaimed: true,
        txHash: record.txHash,
        status,
        source: 'db',
    };
};

export const mapPaymentRequestRecordToHistoryRow = (
    record,
    { role, walletAddress, tokenList = [], chainId } = {},
) => {
    if (!record) return null;

    const requestId = Number(record.requestId);
    if (!Number.isFinite(requestId)) return null;

    const kind = String(record.kind || 'direct').toLowerCase();
    const status = String(record.status || '').toLowerCase();
    const wallet = normalizeAddr(walletAddress);
    const requester = normalizeAddr(record.requester);
    const payer = normalizeAddr(record.payer);

    // History: accepted requests show for both parties (like on-chain resolved).
    // Pending/rejected still appear so users can see their activity.
    if (role === 'sent' && requester !== wallet) return null;
    if (role === 'received' && payer !== wallet) return null;

    const tokenInfo = resolveTokenInfo(record.tokenAddress, tokenList, record);
    const parsedNumber = amountFromRecord(record, tokenInfo.decimals);
    const createdMs = record.createdAt
        ? new Date(record.createdAt).getTime()
        : null;
    const acceptedMs = record.updatedAt && status === 'accepted'
        ? new Date(record.updatedAt).getTime()
        : null;

    const typeLabel =
        kind === 'milestone' ? 'Request · Milestone' : 'Request';

    const meta =
        role === 'sent'
            ? getPaymentRequestMeta({
                  chainId,
                  sender: record.requester,
                  requestId,
                  kind: kind === 'milestone' ? 'milestone' : 'direct',
              })
            : getIncomingPaymentRequestMeta({
                  chainId,
                  requester: record.requester,
                  requestId,
                  kind: kind === 'milestone' ? 'milestone' : 'direct',
              });

    return {
        id: `req-${kind}-${requestId}`,
        requestId,
        requester: record.requester,
        receiver: record.payer,
        sender: record.requester,
        payer: record.payer,
        tokenAddress: record.tokenAddress,
        tokenName: tokenInfo.name,
        tokenImage: tokenInfo.logoURI,
        tokenDecimal: tokenInfo.decimals,
        amount: formatNumber(parsedNumber),
        parsedNumber,
        type: typeLabel,
        requestKind: kind,
        isAccepted: status === 'accepted',
        isClaimed: status === 'accepted',
        paymentTime:
            Number.isFinite(createdMs) && createdMs > 0
                ? Math.floor(createdMs / 1000)
                : undefined,
        createdAt: record.createdAt,
        createdAtMs: Number.isFinite(createdMs) ? createdMs : undefined,
        requestAcceptedAt:
            Number.isFinite(acceptedMs) && acceptedMs > 0 ? acceptedMs : null,
        description: meta.description || record.note || '',
        milestonesNum:
            kind === 'milestone'
                ? Number(record.milestoneCount || 0) || undefined
                : undefined,
        txHash: record.txHash,
        acceptTxHash: record.acceptTxHash,
        status,
        source: 'db',
    };
};

export const mapPaymentRequestRecordForChart = (record, tokenList = []) => {
    if (!record) return null;
    const requestId = Number(record.requestId);
    if (!Number.isFinite(requestId)) return null;
    const tokenInfo = resolveTokenInfo(record.tokenAddress, tokenList, record);
    const parsedNumber = amountFromRecord(record, tokenInfo.decimals);
    const status = String(record.status || '').toLowerCase();
    return {
        id: requestId,
        requestId,
        parsedNumber,
        amount: parsedNumber,
        requester: record.requester,
        receiver: record.payer,
        payer: record.payer,
        tokenAddress: record.tokenAddress,
        token: record.tokenAddress,
        isAccepted: status === 'accepted',
        createdAt: record.createdAt,
        updatedAt: record.updatedAt,
        status,
        source: 'db',
    };
};

export const buildHistoryFromDbRecords = ({
    directRecords = [],
    requestRecords = [],
    role,
    walletAddress,
    tokenList,
    chainId,
}) => {
    const directs = [];
    let totalDirectAmount = 0;

    for (const record of directRecords) {
        const row = mapDirectPaymentRecordToHistoryRow(record, {
            role,
            walletAddress,
            tokenList,
        });
        if (!row) continue;
        directs.push(row);
        if (String(row.type).toLowerCase() === 'direct') {
            totalDirectAmount += Number(row.parsedNumber) || 0;
        }
    }

    const requests = [];
    for (const record of requestRecords) {
        const row = mapPaymentRequestRecordToHistoryRow(record, {
            role,
            walletAddress,
            tokenList,
            chainId,
        });
        if (row) requests.push(row);
    }

    return { rows: [...directs, ...requests], totalDirectAmount };
};

/** Prefer DB row when both exist for the same payment / request key. */
export const mergeHistoryRowsPreferDb = (dbRows = [], chainRows = []) => {
    const map = new Map();
    const keyOf = (row) => {
        const type = String(row?.type || '').toLowerCase();
        if (type.includes('request')) {
            return `req:${row.requestKind || 'direct'}:${row.requestId}`;
        }
        if (type === 'milestone') return `locked:${row.paymentId ?? row.id}`;
        return `direct:${row.paymentId ?? row.id}`;
    };
    for (const row of chainRows) {
        const key = keyOf(row);
        if (key) map.set(key, row);
    }
    for (const row of dbRows) {
        const key = keyOf(row);
        if (key) map.set(key, row);
    }
    return [...map.values()];
};
